import React, { useEffect, useMemo, useState } from "react";
import { Search, User, CalendarDays, X, Users, MapPin, Sparkles, Compass, Star, HeartHandshake, Briefcase } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Role badge styles for regulars (mirrors RegularsPanel.jsx).
const ROLE_STYLES = {
  owner:  { icon: Star,          text: "text-amber-700 dark:text-amber-300", border: "border-amber-500/40", label: "Owner" },
  family: { icon: HeartHandshake, text: "text-rose-700 dark:text-rose-300",   border: "border-rose-500/40",  label: "Family" },
  staff:  { icon: Briefcase,      text: "text-sky-700 dark:text-sky-300",     border: "border-sky-500/40",   label: "Staff" },
};

/**
 * Past-guests quick-fill picker.
 *
 * Lists every guest who has been on the yacht in the PAST but is NOT
 * currently onboard — returning passengers only. Ordered by most recent
 * trip so recent visitors surface first. A "Filter by trip" Select lets
 * the operator narrow the list to guests who sailed on a particular
 * charter (e.g. "everyone from last summer's Owner charter").
 *
 * Selecting a row calls `onPick(guest)` with the previously-recorded
 * name / photo / PIN / age / female values so the parent form can
 * restore them in a single tap. Cabin and stay dates are intentionally
 * NOT restored — those are trip-specific and must always be filled
 * fresh by the operator.
 */
const ALL_TRIPS = "__all__";

export default function PastGuestsPicker({ open, onOpenChange, onPick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [tripId, setTripId] = useState(ALL_TRIPS);
  // "all" | "regulars" | "past" — filters the returned pool. Regulars
  // are always listed FIRST because they're what the crew reaches for
  // most often when placing owners/family/staff onto a new charter.
  const [roleFilter, setRoleFilter] = useState("all");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setQ("");
    setTripId(ALL_TRIPS);
    api.get("/admin/guests/past")
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || "Could not load past guests"))
      .finally(() => setLoading(false));
  }, [open]);

  // Unique trips across all past-guest records, most recent first — used
  // to populate the "Filter by trip" dropdown.
  const trips = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      for (const t of (r.trips_visited || [])) {
        if (!t.id || map.has(t.id)) continue;
        map.set(t.id, t);
      }
    }
    return Array.from(map.values()).sort((a, b) => (b.arrival || "").localeCompare(a.arrival || ""));
  }, [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (s && !(r.name || "").toLowerCase().includes(s)) return false;
      if (tripId !== ALL_TRIPS) {
        const wasOnTrip = (r.trips_visited || []).some((t) => t.id === tripId);
        if (!wasOnTrip) return false;
      }
      if (roleFilter === "regulars" && !r.roster_id) return false;
      if (roleFilter === "past" && r.roster_id) return false;
      if (roleFilter !== "all" && roleFilter !== "regulars" && roleFilter !== "past") {
        // Specific role tab (owner / family / staff)
        if (r.roster_role !== roleFilter) return false;
      }
      return true;
    });
  }, [rows, q, tripId, roleFilter]);

  // Split the list so regulars render at the top with their own header,
  // separated from anonymous past-only returnees. Preserves the source
  // order (backend already puts regulars first, then past guests newest-first).
  const [regulars, pastOnly] = useMemo(() => {
    const reg = filtered.filter((r) => r.roster_id);
    const past = filtered.filter((r) => !r.roster_id);
    return [reg, past];
  }, [filtered]);

  // When a trip filter is active, group results into a single section headed
  // by the trip name; otherwise show a flat "most recent first" list. Either
  // way the row structure stays identical for a stable UI.
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" }); } catch { return ""; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-hidden flex flex-col sm:max-w-lg" data-testid="past-guests-picker">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Users className="h-5 w-5 text-accent" /> Past guests
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Regulars come first — Owners, Family & Staff — then anyone who's sailed with you before. Anyone currently onboard is hidden. Pick a name to reuse their photo, PIN and age; cabin &amp; dates stay empty.
        </p>

        {/* Role filter chips — one-tap collapse to Owners / Family /
            Staff / Past-only so a crew member fetching a specific set
            never has to scroll or type. */}
        <div className="flex flex-wrap gap-1.5 -mt-1" data-testid="past-guests-role-chips">
          {[
            { key: "all", label: "All" },
            { key: "regulars", label: "★ Regulars" },
            { key: "owner", label: "Owners" },
            { key: "family", label: "Family" },
            { key: "staff", label: "Staff" },
            { key: "past", label: "Past guests" },
          ].map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setRoleFilter(c.key)}
              data-testid={`past-guests-role-${c.key}`}
              className={`text-[11px] px-2.5 py-1 border transition-colors ${roleFilter === c.key ? "bg-accent/15 text-accent border-accent/50 font-medium" : "border-border text-muted-foreground hover:border-accent/40"}`}
            >{c.label}</button>
          ))}
        </div>

        {/* Trip filter — collapses the pool to guests who sailed on a
            particular charter, so operators looking up "last summer's
            owner group" don't have to scroll a mile. */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name…"
              className="pl-9"
              data-testid="past-guests-search"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={tripId} onValueChange={setTripId} data-testid="past-guests-trip-filter">
            <SelectTrigger data-testid="past-guests-trip-filter-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TRIPS} data-testid="past-guests-trip-all">All trips</SelectItem>
              {trips.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`past-guests-trip-${t.id}`}>
                  {t.name || "Untitled trip"}{t.arrival ? ` · ${fmtDate(t.arrival)}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary strip — "3 guests · 2 trips visited" so operators
            immediately see how much they've filtered down. */}
        <div className="flex items-center justify-between text-[11px] text-muted-foreground -mt-1">
          <span data-testid="past-guests-summary">
            {loading ? "Loading past guests…" : (
              <>
                <span className="font-medium text-foreground/80">{filtered.length}</span> guest{filtered.length === 1 ? "" : "s"}
                {tripId !== ALL_TRIPS && (
                  <> · filtered by <span className="text-accent">{trips.find((t) => t.id === tripId)?.name || "trip"}</span></>
                )}
              </>
            )}
          </span>
          {(q || tripId !== ALL_TRIPS) && (
            <button
              type="button"
              onClick={() => { setQ(""); setTripId(ALL_TRIPS); }}
              className="text-accent hover:underline"
              data-testid="past-guests-clear-filters"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6" data-testid="past-guests-list">
          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading past guests…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border">
              <Sparkles className="h-5 w-5 mx-auto mb-2 text-accent/60" strokeWidth={1.4} />
              {rows.length === 0
                ? "No returning guests yet — add regulars under the Regulars tab, or wait for the first trip to disembark."
                : "No matches with those filters."}
            </div>
          ) : (
            <div className="space-y-4">
              {regulars.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-accent/80 mb-1.5 flex items-center gap-1.5">
                    <Star className="h-3 w-3" /> Regulars — always aboard
                  </div>
                  <ul className="space-y-1.5">
                    {regulars.map((r) => (
                      <RowLI key={`r-${r.roster_id}`} r={r} onPick={onPick} onOpenChange={onOpenChange} fmtDate={fmtDate} />
                    ))}
                  </ul>
                </div>
              )}
              {pastOnly.length > 0 && (
                <div>
                  {regulars.length > 0 && (
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Past guests</div>
                  )}
                  <ul className="space-y-1.5">
                    {pastOnly.map((r) => (
                      <RowLI key={`p-${r.name}-${r.female}`} r={r} onPick={onPick} onOpenChange={onOpenChange} fmtDate={fmtDate} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end pt-2 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="past-guests-close">Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Row renderer extracted so the regulars / past sections share pixel-identical layout.
function RowLI({ r, onPick, onOpenChange, fmtDate }) {
  const style = r.roster_role ? ROLE_STYLES[r.roster_role] : null;
  return (
    <li>
      <button
        type="button"
        onClick={() => { onPick(r); onOpenChange(false); }}
        className={`w-full text-left flex items-center gap-3 p-2.5 border transition-colors ${r.roster_id ? "border-accent/30 bg-accent/[0.03] hover:border-accent hover:bg-accent/10" : "border-border hover:border-accent hover:bg-accent/5"}`}
        data-testid={`past-guest-${(r.name || "").toLowerCase().replace(/\s+/g, "-")}`}
      >
        {r.image_path ? (
          <img
            src={fileUrl(r.image_path)}
            alt=""
            loading="lazy"
            decoding="async"
            className="w-11 h-11 rounded-full object-cover border border-border flex-shrink-0"
          />
        ) : (
          <div className="w-11 h-11 rounded-full bg-muted border border-border flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-muted-foreground" strokeWidth={1.4} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{r.name}</span>
            {style && (
              <Badge variant="outline" className={`text-[9px] ${style.text} ${style.border}`}>{style.label}</Badge>
            )}
            {r.female && <Badge variant="outline" className="text-[9px] border-pink-500/40 text-pink-700 dark:text-pink-400">F</Badge>}
            {r.visit_count > 1 && (
              <Badge variant="outline" className="text-[9px] border-accent/40 text-accent">{r.visit_count} trips</Badge>
            )}
            {r.age != null && (<Badge variant="outline" className="text-[9px]">Age {r.age}</Badge>)}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            <CalendarDays className="h-3 w-3" />
            {r.visit_count > 0 ? (
              <span>Last: <span className="text-foreground/80 font-medium">{r.last_charter_name || "—"}</span>{r.last_seen ? ` · ${fmtDate(r.last_seen)}` : ""}</span>
            ) : (
              <span className="italic">Never sailed yet — brand-new regular</span>
            )}
            {r.last_cabin_name && (
              <span className="inline-flex items-center gap-1 text-primary/60">
                <Compass className="h-3 w-3" />
                {r.last_cabin_name}
              </span>
            )}
          </div>
          {r.visit_count > 1 && (
            <div className="text-[10px] text-muted-foreground/80 mt-1 flex items-start gap-1">
              <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span className="truncate">
                {(r.trips_visited || []).slice(0, 3).map((t) => t.name || "Trip").join(" · ")}
                {(r.trips_visited || []).length > 3 && ` +${(r.trips_visited || []).length - 3} more`}
              </span>
            </div>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-widest text-accent flex-shrink-0">Use →</span>
      </button>
    </li>
  );
}
